import { createApi } from '@reduxjs/toolkit/query/react';

import { dynamicBaseQuery } from '@redux/baseQuery';
import { participantsSlice } from '@redux/state/participantsSlice';
import { AppState } from '@redux/store';
import { ParticipantStatus } from '@typings/participant';
import { RequestToSpeak, RequestToSpeakStatus } from '@typings/requestToSpeak';
import { requestsToSpeakSlice } from '@redux/state/requestsToSpeakSlice';

export const participantApi = createApi({
  reducerPath: 'participantApi',
  baseQuery: dynamicBaseQuery,
  endpoints: (builder) => ({
    leaveShow: builder.mutation<
      void,
      {
        id: string;
        showId?: string;
        status: ParticipantStatus;
        isAnonymous?: boolean;
        token?: string;
      }
    >({
      query: ({ id, status, isAnonymous }) => {
        if (isAnonymous) {
          return {
            url: `/participant/${id}/leave-show/anonymous`,
            method: 'PUT',
          };
        }
        return {
          url: `/participant/${id}/leave-show`,
          method: 'PUT',
          body: { status },
        };
      },
      onQueryStarted: async (
        { showId, isAnonymous },
        { dispatch, queryFulfilled, getState },
      ) => {
        try {
          await queryFulfilled;
          if (isAnonymous) {
            return;
          }

          if (showId) {
            const { requestsToSpeak } = getState() as AppState;
            const existedRequestToSpeak = Object.values(
              requestsToSpeak.byId as Record<string, RequestToSpeak>,
            ).find(
              (request: RequestToSpeak) =>
                request.showId === showId &&
                (request.status === RequestToSpeakStatus.Pending ||
                  request.status === RequestToSpeakStatus.WaitingForPayment),
            );

            if (existedRequestToSpeak) {
              dispatch(
                requestsToSpeakSlice.actions.setRequestToSpeakDataById({
                  id: existedRequestToSpeak?.id,
                  status: RequestToSpeakStatus.Cancelled,
                }),
              );
            }
          }
        } catch (error) {
          console.log('leaveShow error', error);
        }
      },
    }),
    updateStatus: builder.mutation<
      void,
      { id: string; status: ParticipantStatus }
    >({
      query: ({ id, status }) => ({
        url: `/participant/${id}/status`,
        method: 'PUT',
        body: { status },
      }),
      onQueryStarted: async ({ id, status }, { dispatch, queryFulfilled }) => {
        try {
          await queryFulfilled;
          dispatch(
            participantsSlice.actions.setParticipantDataById({
              id,
              status,
            }),
          );
        } catch (error) {
          console.log('updateStatus error', error);
        }
      },
    }),
  }),
});

export const { useLeaveShowMutation, useUpdateStatusMutation } = participantApi;
